import React, { FC, useState, useEffect } from 'react';
import { Button } from '../ui/button';
import axios from 'axios';
import { FiFilter } from 'react-icons/fi';
import { Label } from '../ui/label';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Checkbox } from '../ui/checkbox';
import { useSelector } from 'react-redux';
import { RootState } from '@/src/State/store';

interface Channel {
  channel_id: number;
  channel_name: string;
}

interface FilterDataState {
  filter: string;
  subFilter: string;
}

interface OperatorTemplateDropdownProps {
  setFilterData: React.Dispatch<React.SetStateAction<FilterDataState>>;
  filterData: FilterDataState;
  dateList: string[];
}

type MainFilterOption = 'None' | 'Channel' | 'Status' | 'Last Edited';
type SubFilterOption = string;

const OperatorTemplateDropdown: FC<OperatorTemplateDropdownProps> = ({
  setFilterData,
  filterData,
  dateList,
}) => {
  const [selectedFilter, setSelectedFilter] =
    useState<MainFilterOption>('None');
  const [selectedSubFilter, setSelectedSubFilter] =
    useState<SubFilterOption>('');
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [channelList, setChannelList] = useState<Channel[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [channelDropdownOpen, setChannelDropdownOpen] = useState(false);
  const apiUrlAdvAcc = useSelector(
    (state: RootState) => state.authentication.apiURL,
  );

  // Fetch channel list
  useEffect(() => {
    const fetchChannelList = async () => {
      try {
        const response = await axios.get(`${apiUrlAdvAcc}/GetChannelList`);
        if (response?.data?.channelList) {
          setChannelList(response.data.channelList);
        }
      } catch (error) {
        console.error('Error fetching channel list:', error);
      }
    };
    if (apiUrlAdvAcc) fetchChannelList();
  }, [apiUrlAdvAcc]);

  // Sync with parent — restore UI state from filterData whenever it changes
  useEffect(() => {
    setSelectedFilter(filterData.filter as MainFilterOption);

    if (filterData.filter === 'Channel' && filterData.subFilter) {
      setSelectedChannels(filterData.subFilter.split(','));
      setSelectedSubFilter('');
    } else {
      setSelectedSubFilter(filterData.subFilter);
      setSelectedChannels([]);
    }
  }, [filterData]);

  const handleMainFilterChange = (filter: MainFilterOption) => {
    setSelectedFilter(filter);
    setSelectedSubFilter('');
    setSelectedChannels([]);

    if (filter === 'Channel') {
      // Set first channel as default if available
      if (channelList.length > 0) {
        const defaultChannel = channelList[0].channel_name;
        setSelectedChannels([defaultChannel]);
        setFilterData({ filter, subFilter: defaultChannel });
      } else {
        setFilterData({ filter, subFilter: '' });
      }
    } else if (filter === 'Status') {
      const defaultStatus = 'Approved';
      setSelectedSubFilter(defaultStatus);
      setFilterData({ filter, subFilter: defaultStatus });
    } else if (filter === 'Last Edited') {
      if (dateList.length > 0) {
        const defaultDate = dateList[0];
        setSelectedSubFilter(defaultDate);
        setFilterData({ filter: 'Last Edited', subFilter: defaultDate });
      } else {
        setFilterData({ filter: 'Last Edited', subFilter: '' });
      }
    } else {
      setFilterData({ filter: 'None', subFilter: '' });
    }

    setIsOpen(true);
  };

  // Handle channel selection in dropdown (multi-select)
  const handleChannelSelect = (channel: string) => {
    let newChannels: string[];

    if (selectedChannels.includes(channel)) {
      newChannels = selectedChannels.filter((c) => c !== channel);
    } else {
      newChannels = [...selectedChannels, channel];
    }

    setSelectedChannels(newChannels);

    const channelValue = newChannels.join(',');
    setFilterData({ filter: 'Channel', subFilter: channelValue });

    // Keep dropdown open for multi-select
    setChannelDropdownOpen(true);
  };

  // Handle subfilter change for non-Channel filters
  const handleSubFilterChange = (value: SubFilterOption) => {
    setSelectedSubFilter(value);
    setFilterData({ filter: selectedFilter, subFilter: value });
    setIsOpen(true);
  };

  // Get display text for filter button
  const getFilterButtonText = () => {
    if (selectedFilter === 'None' || !filterData.subFilter) {
      return 'Filter';
    }

    if (selectedFilter === 'Channel' && selectedChannels.length > 0) {
      return `${selectedFilter}: ${selectedChannels.join(', ')}`;
    }

    return `${selectedFilter}: ${filterData.subFilter}`;
  };

  // Get display text for channel dropdown
  const getChannelDisplayText = () => {
    if (selectedChannels.length === 0) {
      return 'Select channels';
    }
    return selectedChannels.join(', ');
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant='outline'
          className='w-full mb-6 ml-4 mt-[-6] text-[#020617] font-medium text-[14px] py-2 px-3'
        >
          <FiFilter
            style={{ width: '14px', height: '14px' }}
            className='mr-1 text-[#020617]'
          />
          {getFilterButtonText()}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className='w-64 p-3 rounded-md shadow-md space-y-4 -mt-12 mr-[105px]'>
        {/* Criteria */}
        <div className='flex w-full'>
          <div className='basis-1/3 flex justify-center items-center'>
            <Label className='text-sm text-[#020617]'>Criteria</Label>
          </div>
          <div className='basis-2/3 flex justify-center items-center'>
            <Select
              value={selectedFilter}
              onValueChange={(value) =>
                handleMainFilterChange(value as MainFilterOption)
              }
            >
              <SelectTrigger className='text-gray-500 w-full h-8'>
                <SelectValue placeholder='Select' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem className='cursor-pointer' value='Channel'>
                  Channel
                </SelectItem>
                <SelectItem className='cursor-pointer' value='Status'>
                  Status
                </SelectItem>
                <SelectItem className='cursor-pointer' value='Last Edited'>
                  Last Edited
                </SelectItem>
                <SelectItem className='cursor-pointer' value='None'>
                  None
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Subfilter */}
        {selectedFilter !== 'None' && (
          <div className='flex w-full'>
            <div className='basis-1/3 flex justify-center items-center'>
              <Label className='text-sm text-[#020617]'>Subfilter</Label>
            </div>
            <div className='basis-2/3 flex justify-center items-center'>
              {selectedFilter === 'Channel' && (
                <Select
                  open={channelDropdownOpen}
                  onOpenChange={setChannelDropdownOpen}
                  value={selectedChannels.length > 0 ? 'selected' : ''}
                >
                  <SelectTrigger className='text-gray-500 w-[150px] h-8'>
                    <SelectValue>
                      <span
                        className={
                          selectedChannels.length === 0 ? 'text-gray-500' : ''
                        }
                      >
                        {getChannelDisplayText()}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {channelList.map((channel) => (
                      <div
                        key={channel.channel_id}
                        className='flex items-center space-x-2 px-2 py-1.5 cursor-pointer hover:bg-gray-100'
                        onClick={(e) => {
                          e.preventDefault();
                          handleChannelSelect(channel.channel_name);
                        }}
                      >
                        <Checkbox
                          id={`template-channel-${channel.channel_id}`}
                          checked={selectedChannels.includes(
                            channel.channel_name,
                          )}
                          onCheckedChange={() =>
                            handleChannelSelect(channel.channel_name)
                          }
                        />
                        <label
                          htmlFor={`template-channel-${channel.channel_id}`}
                          className='text-sm font-medium leading-none cursor-pointer flex-1'
                        >
                          {channel.channel_name}
                        </label>
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {selectedFilter === 'Status' && (
                <Select
                  value={selectedSubFilter}
                  onValueChange={(value) =>
                    handleSubFilterChange(value as SubFilterOption)
                  }
                >
                  <SelectTrigger className='text-gray-500 w-[150px] h-8'>
                    <SelectValue placeholder='Select status'>
                      <span className='truncate block'>
                        {selectedSubFilter || 'Select status'}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem className='cursor-pointer' value='Approved'>
                      APPROVED
                    </SelectItem>
                    <SelectItem className='cursor-pointer' value='Pending'>
                      PENDING
                    </SelectItem>
                    <SelectItem className='cursor-pointer' value='Rejected'>
                      REJECTED
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}

              {selectedFilter === 'Last Edited' && (
                <Select
                  value={selectedSubFilter}
                  onValueChange={(value) =>
                    handleSubFilterChange(value as SubFilterOption)
                  }
                >
                  <SelectTrigger className='text-gray-500 w-[150px] h-8'>
                    <SelectValue placeholder='Select date'>
                      <span className='truncate block'>
                        {selectedSubFilter || 'Select date'}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className='max-h-[200px] overflow-y-auto'>
                    {dateList.map((date: string) => (
                      <SelectItem
                        key={date}
                        value={date}
                        className='cursor-pointer'
                      >
                        {date}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default OperatorTemplateDropdown;
